/**
 * # NavItem
 *
 * React component that represents a navigation item.
 *
 * ## Props
 *
 * - `as?: React.ElementType`: The wrapper element type. Defaults to `Link`.
 * - `'aria-label': string`: The ARIA label for accessibility.
 * - `active: boolean`: Whether the navigation item is active.
 * - `disabled: boolean`: Whether the navigation item is disabled.
 * - `icon: IconProp`: The icon to be displayed.
 * - `iconPosition: 'left' | 'right'`: The position of the icon.
 * - `iconStyle: IconStyle`: The style of the icon.
 * - `children: React.ReactNode`: The content of the navigation item.
 * - `className: string`: Additional CSS class names.
 * - `stateless: boolean`: Whether the navigation item is stateless.
 *
 * ## Usage
 *
 * ```tsx
 * import NavItem from './NavItem';
 *
 * const Example = () => {
 *   return (
 *     <NavItem
 *       'aria-label'="Example Nav Item"
 *       active={true}
 *       disabled={false}
 *       icon="example-icon"
 *       iconPosition="left"
 *       iconStyle="regular"
 *       className="example-class"
 *       stateless={false}
 *     >
 *       Example Navigation Item
 *     </NavItem>
 *   );
 * };
 * ```
 */

import type {ElementType, ComponentPropsWithoutRef} from 'react';
import React, {forwardRef} from 'react';
import classnames from 'classnames';
import {Link} from 'react-router';
import makeConstants from 'lib/makeConstants';

import type {IconStyle, IconProp} from '../Icon/Icon.react';
import Icon from '../Icon/Icon.react';

import './nav-item.scss';

const positions = ['left', 'right'] as const;

const position = makeConstants(...positions);

interface BaseProps extends PropsWithChildrenRequired, PropsWithClassNameOptional {
  as?: ElementType;
  'aria-label'?: string;
  active?: boolean;
  disabled?: boolean;
  icon?: IconProp;
  iconPosition?: 'left' | 'right';
  iconStyle?: IconStyle;
  stateless?: boolean;
}

// Allow any HTML attributes that the WrapperElement might accept
type Props<T extends ElementType = typeof Link> = BaseProps &
  Omit<ComponentPropsWithoutRef<T>, keyof BaseProps>;

export const NavItem = forwardRef(
  (
    {
      as: WrapperElement = Link,
      'aria-label': ariaLabel,
      active = false,
      disabled = false,
      icon,
      iconPosition = 'left',
      iconStyle = 'regular',
      children,
      className,
      stateless = false,
      onKeyDown,
      ...rest
    }: Props,
    ref
  ) => {
    const style = icon === 'caret-down' ? 'solid' : iconStyle;
    const leftIcon = icon && iconPosition === position.left;
    const rightIcon = icon && iconPosition === position.right;
    const isStandaloneIcon = !children;
    const iconSize = isStandaloneIcon ? 'standalone' : undefined;

    // Handle keyboard events for accessibility
    const handleKeyDown = (e: React.KeyboardEvent) => {
      // Call any existing onKeyDown handler first
      if (onKeyDown) {
        onKeyDown(e);
      }

      // For button-like behavior when using 'div' as wrapper
      if (WrapperElement === 'div' && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        // Trigger click event for dropdown compatibility
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true
        });
        e.currentTarget.dispatchEvent(clickEvent);
      }
    };

    return (
      <WrapperElement
        className={classnames('a-nav-item', className, {
          'a-nav-item--active': active,
          'a-nav-item--disabled': disabled,
          'a-nav-item--has-states': !stateless
        })}
        ref={ref}
        aria-label={ariaLabel}
        onKeyDown={handleKeyDown}
        {...rest}
      >
        {leftIcon && (
          <Icon
            aria-hidden
            icon={icon}
            iconStyle={style}
            size={iconSize}
            className={children ? 'mr-x1' : ''}
          />
        )}
        {children}
        {rightIcon && (
          <Icon
            aria-hidden
            icon={icon}
            iconStyle={style}
            size={iconSize}
            className={children ? 'ml-x1' : ''}
          />
        )}
      </WrapperElement>
    );
  }
);

NavItem.displayName = 'NavItem';

export default NavItem;
